export interface AnalysisResult {
  id: string;
  title: string;
  description: string;
  value: string;
  aiExplanation: string;
  visualMatches?: VisualMatch[];
  imageCompare?: string;
  comparisonImages?: { [angle: string]: string };
  imageUrl?: string;
  apiProvider: ApiProvider;
  timestamp: number;
}

export interface VisualMatch {
  title: string;
  link: string;
  source_icon?: string;
  thumbnail?: string;
  source?: string;
  price?: string;
  position?: number;
}

export interface SearchApiItem {
  position: number;
  title: string;
  link: string;
  source: string;
  price?: string;
  extracted_price?: number;
  currency?: string;
  stock_information?: string;
  thumbnail: string;
  source_icon?: string;
  image?: {
    link: string;
    height: number;
    width: number;
  };
}

export interface SearchApiResult extends AnalysisResult {
  visual_matches: SearchApiItem[];
}

export interface ScanHistoryItem extends AnalysisResult {
  isFavorite: boolean;
}

export enum ApiProvider {
  GEMINI = 'Gemini',
  SERPAPI = 'SerpAPI',
  SEARCHAPI = 'SearchAPI',
  DECODO = 'DECODO',
  OPENLENS = 'OpenLens',
  GROK = 'Grok',
}

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
}

export type Theme = 'light' | 'dark';

export enum UserRole {
  GUEST = 'guest',
  USER = 'user',
  PAID = 'paid',
  ADMIN = 'admin',
}

export interface UserPermissions {
  canUseGemini: boolean;
  canUseSerpAPI: boolean;
  canUseSearchAPI: boolean;
  canUseDecodo: boolean;
  canUseOpenLens: boolean;
  canUseGrok: boolean;
  canUseImageEnhancer: boolean;
  canAccessAdminPanel: boolean;
  allowedProviders: ApiProvider[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  role: UserRole;
  subscription_end_date?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile?: (data: { name: string; email: string }) => Promise<void>;
  updatePassword?: (newPassword: string) => Promise<void>;
  resetPassword?: (email: string) => Promise<void>;
  deleteAccount?: () => Promise<void>;
  isLoading: boolean;
  forceRefreshProfile?: () => Promise<void>;
}

export interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

export interface ToastContextType {
  showToast: (type: ToastMessage['type'], title: string, message?: string) => void;
}

// Extended interface for Decodo-specific match data
export interface DecodoMatch extends VisualMatch {
  rating?: number;
  reviews?: number;
  availability?: string;
  condition?: string;
  shipping?: string;
  category?: 'shopping' | 'visual' | 'similar' | 'organic';
  extracted_price?: number;
  currency?: string;
  domain?: string;
}

// Enhanced analysis result for Decodo with additional structured data
export interface DecodoAnalysisResult extends AnalysisResult {
  priceAnalysis?: {
    min: number;
    max: number;
    avg: number;
    count: number;
    currency: string;
  };
  categorizedMatches?: {
    shopping: DecodoMatch[];
    visual: DecodoMatch[];
    similar: DecodoMatch[];
    organic: DecodoMatch[];
  };
  knowledgeGraph?: any;
  relatedSearches?: string[];
}
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  created_at: string;
  last_login?: string;
  role: UserRole;
  subscription_end_date?: string;
}

export interface RecentUpload {
  id: string;
  filename: string;
  uploader_name: string;
  uploader_id: string;
  upload_time: string;
  file_size: number;
  status: 'processing' | 'complete' | 'failed';
  api_provider?: string;
  result?: any; // Keep this for backward compatibility or if 'result' is used elsewhere
  title?: string;
  description?: string; // Add description
  value?: string; // Add value
  ai_explanation?: string; // Add AI explanation
}

// Export inventory types
export * from './inventory';
